// ----------------------------------------------------------------------------
// Spring Calculator: Store lookup
// ----------------------------------------------------------------------------
//
// Resolves FOX part numbers to purchasable variants on the current store using
// public storefront endpoints, so no product has to be picked by hand and the
// same catalogue works on every store:
//
//   1. /search/suggest.json?q=<sku>   -> product handles whose variants match
//   2. /products/<handle>.js          -> full variants with exact SKUs
//
// Responses are cached per SKU and per handle for the life of the page.
//
// ----------------------------------------------------------------------------

import { formatMoney } from '@shopify/theme-currency';

const normaliseSku = (sku) => String(sku || '').trim().toUpperCase();

const fetchJson = (url) => fetch(url, { headers: { Accept: 'application/json' } })
    .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
        return response.json();
    });

/**
 * @param {Object} options
 * @param {string} options.predictiveSearchUrl  e.g. "/search/suggest.json"
 * @param {string} options.rootUrl              e.g. "/" or "/fr/"
 * @param {string} options.moneyFormat          Shopify money format string
 */
export const createStoreLookup = ({ predictiveSearchUrl, rootUrl, moneyFormat }) => {
    const productCache = new Map(); // handle -> Promise<product>
    const skuCache = new Map();     // sku    -> Promise<variant|null>

    const fetchProduct = (handle) => {
        if (!productCache.has(handle)) {
            productCache.set(handle, fetchJson(`${rootUrl}products/${handle}.js`));
        }
        return productCache.get(handle);
    };

    const searchHandles = (sku) => {
        const params = new URLSearchParams({
            q: sku,
            'resources[type]': 'product',
            'resources[limit]': '5',
            'resources[options][fields]': 'variants.sku',
            'resources[options][unavailable_products]': 'show',
        });

        return fetchJson(`${predictiveSearchUrl}?${params}`)
            .then((data) => (data?.resources?.results?.products || []).map((product) => product.handle));
    };

    const toVariant = (product, variant) => ({
        id: variant.id,
        sku: variant.sku,
        title: variant.title,
        productTitle: product.title,
        url: `${product.url}?variant=${variant.id}`,
        price: formatMoney(variant.price, moneyFormat),
        available: Boolean(variant.available),
        image: variant.featured_image?.src || product.featured_image || null,
        source: 'store',
    });

    const findVariant = (sku) => {
        if (!skuCache.has(sku)) {
            const promise = searchHandles(sku)
                .then((handles) => Promise.all(handles.map(fetchProduct)))
                .then((products) => {
                    for (const product of products) {
                        const variant = product.variants.find((candidate) => normaliseSku(candidate.sku) === sku);
                        if (variant) return toVariant(product, variant);
                    }
                    return null;
                })
                .catch((error) => {
                    console.warn(`Spring calculator: lookup failed for ${sku}`, error);
                    skuCache.delete(sku); // allow a retry on the next calculation
                    return null;
                });

            skuCache.set(sku, promise);
        }
        return skuCache.get(sku);
    };

    /**
     * Resolve a list of part numbers to store variants.
     *
     * @param {string[]} skus
     * @returns {Promise<Object>} map of normalised SKU -> variant (unresolved SKUs are omitted)
     */
    const resolveSkus = async (skus) => {
        const unique = [...new Set(skus.map(normaliseSku).filter(Boolean))];
        const variants = await Promise.all(unique.map(findVariant));

        return unique.reduce((index, sku, i) => {
            if (variants[i]) index[sku] = variants[i];
            return index;
        }, {});
    };

    return { resolveSkus };
};
